import { Badge, cn } from "@fox1ck-org/ui";

const healthStyles: Record<string, { variant: "ok" | "danger" | "info" | "warn" | "neutral"; dot: string }> = {
  Healthy: { variant: "ok", dot: "bg-green-400" },
  Progressing: { variant: "info", dot: "bg-blue-400" },
  Degraded: { variant: "danger", dot: "bg-red-400" },
  Suspended: { variant: "neutral", dot: "bg-gray-400" },
  Missing: { variant: "warn", dot: "bg-yellow-400" },
  Unknown: { variant: "neutral", dot: "bg-gray-400" },
};

const syncStyles: Record<string, { variant: "ok" | "warn" | "neutral" }> = {
  Synced: { variant: "ok" },
  OutOfSync: { variant: "warn" },
  Unknown: { variant: "neutral" },
};

const healthLabels: Record<string, string> = {
  Healthy: "Исправно",
  Progressing: "Обновляется",
  Degraded: "Деградация",
  Suspended: "Приостановлено",
  Missing: "Отсутствует",
  Unknown: "Неизвестно",
};

const syncLabels: Record<string, string> = {
  Synced: "Синхронизировано",
  OutOfSync: "Не синхронизировано",
  Unknown: "Неизвестно",
};

const operationLabels: Record<string, string> = {
  pending: "Ожидание",
  running: "Выполняется",
  completed: "Завершено",
  failed: "Сбой",
  cancelled: "Отменено",
};

/** A health status from ArgoCD in words; a value we do not know stays as it came. */
export function healthLabel(status: string): string {
  return healthLabels[status] ?? status;
}

interface StatusBadgeProps {
  status: string;
  type: "health" | "sync";
  className?: string;
}

export function StatusBadge({ status, type, className }: StatusBadgeProps) {
  if (type === "health") {
    const style = healthStyles[status] ?? healthStyles.Unknown!;
    return (
      <Badge variant={style.variant} className={cn("gap-1.5", className)}>
        <span className={cn("h-1.5 w-1.5 rounded-full", style.dot)} />
        {healthLabel(status)}
      </Badge>
    );
  }

  const style = syncStyles[status] ?? syncStyles.Unknown!;
  return (
    <Badge variant={style.variant} className={className}>
      {syncLabels[status] ?? status}
    </Badge>
  );
}

interface OperationStatusBadgeProps {
  status: string;
  className?: string;
}

export function OperationStatusBadge({ status, className }: OperationStatusBadgeProps) {
  const styles: Record<string, "warn" | "info" | "ok" | "danger" | "neutral"> = {
    pending: "warn",
    running: "info",
    completed: "ok",
    failed: "danger",
    cancelled: "neutral",
  };

  return (
    <Badge variant={styles[status] ?? "neutral"} className={className}>
      {operationLabels[status] ?? status}
    </Badge>
  );
}

interface OperationTypeBadgeProps {
  type: string;
  className?: string;
}

export function OperationTypeBadge({ type, className }: OperationTypeBadgeProps) {
  const labels: Record<string, string> = {
    backup: "Резервная копия",
    restore: "Восстановление",
    prod_to_dev: "Из прода в дев",
  };
  const variants: Record<string, "info" | "neutral" | "warn"> = {
    backup: "info",
    restore: "neutral",
    prod_to_dev: "warn",
  };

  return (
    <Badge variant={variants[type] ?? "neutral"} className={className}>
      {labels[type] ?? type}
    </Badge>
  );
}
